import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AccountPanelProvider } from './context/AccountPanelContext';
import { WishlistProvider } from './context/WishlistContext';
import { ToastProvider } from './context/ToastContext';
import { SiteLogoProvider } from './context/SiteLogoContext';
import { CheckoutProvider } from './context/CheckoutContext';
import { SearchProvider } from './context/SearchContext';
import { StickyMobileBarProvider } from './context/StickyMobileBarContext';
import ErrorBoundary from './components/common/ErrorBoundary/ErrorBoundary';
import PageViewTracker from './components/analytics/PageViewTracker';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import CategoryRoute from './components/routing/CategoryRoute';
import SubcategoryRoute from './components/routing/SubcategoryRoute';
import ProductRoute from './components/routing/ProductRoute';
import AccountPanel from './components/account/AccountPanel';
import NotFound from './pages/NotFound/NotFound';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import AuthCallback from './components/auth/AuthCallback';
import AccountDashboard from './pages/Account/AccountDashboard';
import AccountOrders from './pages/Account/AccountOrders';
import AccountOrderDetail from './pages/Account/AccountOrderDetail';
import AccountOrderReturn from './pages/Account/AccountOrderReturn';
import AccountAddresses from './pages/Account/AccountAddresses';
import AccountWishlist from './pages/Account/AccountWishlist';
import AccountSettings from './pages/Account/AccountSettings';
import AccountChangePassword from './pages/Account/AccountChangePassword';
import Wishlist from './pages/Wishlist/Wishlist';
import CartRecover from './pages/Cart/CartRecover';
import Shipping from './pages/Shipping/Shipping';
import Returns from './pages/Returns/Returns';
import SizeGuide from './pages/SizeGuide/SizeGuide';
import Contact from './pages/Contact/Contact';
import FAQ from './pages/FAQ/FAQ';
import About from './pages/About/About';
import Story from './pages/Story/Story';
import MenRedirect from './pages/MenRedirect/MenRedirect';
import Privacy from './pages/Privacy/Privacy';
import Terms from './pages/Terms/Terms';
import Cookies from './pages/Cookies/Cookies';
import Newsletter from './pages/Newsletter/Newsletter';
import RecentlyViewed from './pages/RecentlyViewed/RecentlyViewed';
import OrderSuccess from './pages/OrderSuccess/OrderSuccess';
import WishlistDrawer from './components/wishlist/WishlistDrawer/WishlistDrawer';
import CartDrawer from './components/cart/CartDrawer/CartDrawer';
import { useWishlist } from './context/WishlistContext';
import { useCart } from './context/CartContext';
import { setCloseCartRef } from './context/WishlistContext';
import { warmBackend } from './config/api';
import PageLoaderFallback from './components/common/Layout/PageLoaderFallback';
import './App.css';

// Phase 4: Route-level code splitting for heavy routes
// Home: lazy for smaller initial bundle (LCP/INP); minimal fallback to avoid CLS
const Home = lazy(() => import('./pages/Home/Home'));
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard/AdminDashboardEnhanced'));
const ProductList = lazy(() => import('./pages/Admin/AdminProducts/ProductListEnhanced'));
const ProductForm = lazy(() => import('./pages/Admin/AdminProducts/ProductForm'));
const CategoryList = lazy(() => import('./pages/Admin/AdminCategories/CategoryListEnhanced'));
const CategoryForm = lazy(() => import('./pages/Admin/AdminCategories/CategoryForm'));
const SubcategoryList = lazy(() => import('./pages/Admin/AdminSubcategories/SubcategoryList'));
const SubcategoryForm = lazy(() => import('./pages/Admin/AdminSubcategories/SubcategoryForm'));
const CouponList = lazy(() => import('./pages/Admin/AdminCoupons/CouponList'));
const CouponForm = lazy(() => import('./pages/Admin/AdminCoupons/CouponForm'));
const OrderList = lazy(() => import('./pages/Admin/AdminOrders/OrderList'));
const OrderDetail = lazy(() => import('./pages/Admin/AdminOrders/OrderDetail'));
const UserList = lazy(() => import('./pages/Admin/AdminUsers/UserList'));
const AddAdmin = lazy(() => import('./pages/Admin/AdminUsers/AddAdmin'));
const InventoryList = lazy(() => import('./pages/Admin/AdminInventory/InventoryList'));
const WarehouseList = lazy(() => import('./pages/Admin/AdminWarehouses/WarehouseList'));
const CampaignList = lazy(() => import('./pages/Admin/AdminCampaigns/CampaignList'));
const BannerList = lazy(() => import('./pages/Admin/AdminBanners/BannerList'));
const AnnouncementBarList = lazy(() => import('./pages/Admin/AdminAnnouncements/AnnouncementBarList'));
const ReviewList = lazy(() => import('./pages/Admin/AdminReviews/ReviewList'));
const PopupCampaignList = lazy(() => import('./pages/Admin/AdminPopupCampaigns/PopupCampaignList'));
const HomepageImages = lazy(() => import('./pages/Admin/AdminHomepage/HomepageImages'));
const ContentList = lazy(() => import('./pages/Admin/AdminContent/ContentList'));
const ContentForm = lazy(() => import('./pages/Admin/AdminContent/ContentForm'));
const ReportsPage = lazy(() => import('./pages/Admin/AdminReports/ReportsPage'));
const AdminAnalytics = lazy(() => import('./pages/Admin/AdminAnalytics/AdminAnalytics'));
const LogsList = lazy(() => import('./pages/Admin/AdminLogs/LogsList'));
const ReturnsList = lazy(() => import('./pages/Admin/AdminReturns/ReturnsList'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const ProductDetail = lazy(() => import('./pages/ProductDetail/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart/Cart'));
// INP: lazy-load heavy listing/search routes to minimize initial bundle
const Women = lazy(() => import('./pages/Women/Women'));
const NewArrivals = lazy(() => import('./pages/NewArrivals/NewArrivals'));
const Bestsellers = lazy(() => import('./pages/Bestsellers/Bestsellers'));
const Shop = lazy(() => import('./pages/Shop/Shop'));
const Search = lazy(() => import('./pages/Search/Search'));

function WishlistDrawerWrapper() {
  const { isWishlistDrawerOpen, closeWishlistDrawer } = useWishlist();
  return <WishlistDrawer isOpen={isWishlistDrawerOpen} onClose={closeWishlistDrawer} />;
}

function CartDrawerWrapper() {
  const { isCartOpen, closeCart } = useCart();
  return <CartDrawer isOpen={isCartOpen} onClose={closeCart} />;
}

// Component to set up mutual exclusivity between drawers
function DrawerCoordinator() {
  const { closeCart } = useCart();
  useWishlist(); // keep context mounted for ref coordination
  
  // Set up ref so WishlistContext can close cart drawer
  useEffect(() => {
    setCloseCartRef(closeCart);
    
    return () => {
      setCloseCartRef(null);
    };
  }, [closeCart]);
  
  return null;
}

function App() {
  // Track scroll position for iOS Safari scroll lock
  useEffect(() => {
    const handleScroll = () => {
      document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Warm backend (e.g. Render) on first load so cold start happens early
  useEffect(() => {
    warmBackend();
  }, []);

  return (
    <HelmetProvider>
      {/* Router must wrap everything that uses React Router (including ErrorBoundary links) */}
      <Router>
        <PageViewTracker />
        <ErrorBoundary>
          <AuthProvider>
            <CartProvider>
              <WishlistProvider>
                <ToastProvider>
                  <SiteLogoProvider>
                  <CheckoutProvider>
                    <SearchProvider>
                      <StickyMobileBarProvider>
                        <AccountPanelProvider>
                        <AccountPanel />
                        <DrawerCoordinator />
                        <WishlistDrawerWrapper />
                        <CartDrawerWrapper />
                      <Routes>
              <Route path="/" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Home /></Suspense>} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/account" element={<PrivateRoute><AccountDashboard /></PrivateRoute>} />
                  <Route path="/account/orders" element={<PrivateRoute><AccountOrders /></PrivateRoute>} />
                  <Route path="/account/orders/:orderId" element={<PrivateRoute><AccountOrderDetail /></PrivateRoute>} />
                  <Route path="/account/orders/:orderId/return" element={<PrivateRoute><AccountOrderReturn /></PrivateRoute>} />
                  <Route path="/account/addresses" element={<PrivateRoute><AccountAddresses /></PrivateRoute>} />
                  <Route path="/account/wishlist" element={<PrivateRoute><AccountWishlist /></PrivateRoute>} />
                  <Route path="/account/settings" element={<PrivateRoute><AccountSettings /></PrivateRoute>} />
                  <Route path="/account/change-password" element={<PrivateRoute><AccountChangePassword /></PrivateRoute>} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/cart" element={<Suspense fallback={<PageLoaderFallback label="Loading cart" />}><Cart /></Suspense>} />
                  <Route path="/cart/recover" element={<CartRecover />} />
              <Route path="/search" element={<Suspense fallback={<PageLoaderFallback label="Loading search" />}><Search /></Suspense>} />
                  {/* Men routes — explain focus on women's jewelry and guide users */}
                  <Route path="/men" element={<MenRedirect />} />
                  <Route path="/men/:subcategorySlug" element={<MenRedirect />} />
                  <Route path="/shop/men" element={<MenRedirect />} />
                  <Route path="/shop/men/:subcategorySlug" element={<MenRedirect />} />
                  {/* Women (primary category) — lazy for INP / smaller initial bundle */}
                  <Route path="/women" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense>} />
                  <Route path="/women/:subcategorySlug" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense>} />
                  <Route path="/shop/women" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense>} />
                  <Route path="/shop/women/:subcategorySlug" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense>} />
                  <Route path="/new-arrivals" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><NewArrivals /></Suspense>} />
                  <Route path="/bestsellers" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Bestsellers /></Suspense>} />
                  <Route path="/shipping" element={<Shipping />} />
                  <Route path="/returns" element={<Returns />} />
                  <Route path="/size-guide" element={<SizeGuide />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/story" element={<Story />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/cookies" element={<Cookies />} />
                  <Route path="/shop" element={<Suspense fallback={<PageLoaderFallback label="Loading" />}><Shop /></Suspense>} />
                  {/* Dynamic category (only women valid; men already redirected above) */}
                  <Route path="/:categorySlug" element={<CategoryRoute><Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense></CategoryRoute>} />
                  <Route path="/:categorySlug/:subcategorySlug" element={<SubcategoryRoute><Suspense fallback={<PageLoaderFallback label="Loading" />}><Women /></Suspense></SubcategoryRoute>} />
                  <Route path="/collections" element={<Navigate to="/shop/women" replace />} />
                  <Route path="/collections/all" element={<Navigate to="/shop" replace />} />
                  <Route path="/collections/best-sellers" element={<Navigate to="/bestsellers" replace />} />
                  <Route path="/newsletter" element={<Newsletter />} />
                  <Route path="/recently-viewed" element={<RecentlyViewed />} />
                  <Route path="/product/:slug" element={<ProductRoute><Suspense fallback={<PageLoaderFallback label="Loading product" />}><ProductDetail /></Suspense></ProductRoute>} />
                  <Route path="/checkout" element={<Suspense fallback={<PageLoaderFallback label="Loading checkout" />}><Checkout /></Suspense>} />
                  <Route path="/order-success/:orderId" element={<OrderSuccess />} />
                  <Route path="/order/success/:orderId" element={<OrderSuccess />} />
                  <Route path="/order/failure/:orderId" element={<OrderSuccess />} />
                  {/* Admin Routes - Phase 4: lazy-loaded */}
                  <Route path="/admin" element={<AdminRoute><Suspense fallback={<PageLoaderFallback label="Loading admin" />}><AdminLayout /></Suspense></AdminRoute>}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<ProductList />} />
                    <Route path="products/new" element={<ProductForm />} />
                    <Route path="products/edit/:id" element={<ProductForm />} />
                    <Route path="categories" element={<CategoryList />} />
                    <Route path="categories/new" element={<CategoryForm />} />
                    <Route path="categories/edit/:id" element={<CategoryForm />} />
                    <Route path="subcategories" element={<SubcategoryList />} />
                    <Route path="subcategories/new" element={<SubcategoryForm />} />
                    <Route path="subcategories/edit/:id" element={<SubcategoryForm />} />
                    <Route path="coupons" element={<CouponList />} />
                    <Route path="coupons/new" element={<CouponForm />} />
                    <Route path="coupons/edit/:id" element={<CouponForm />} />
                    <Route path="orders" element={<OrderList />} />
                    <Route path="orders/:id" element={<OrderDetail />} />
                    <Route path="returns" element={<ReturnsList />} />
                    <Route path="users" element={<UserList />} />
                    <Route path="users/add" element={<AddAdmin />} />
                    <Route path="inventory" element={<InventoryList />} />
                    <Route path="warehouses" element={<WarehouseList />} />
                    <Route path="campaigns" element={<CampaignList />} />
                    <Route path="banners" element={<BannerList />} />
                    <Route path="announcement-bar" element={<AnnouncementBarList />} />
                    <Route path="reviews" element={<ReviewList />} />
                    <Route path="popup-campaigns" element={<PopupCampaignList />} />
                    <Route path="homepage" element={<HomepageImages />} />
                    <Route path="content" element={<ContentList />} />
                    <Route path="content/:key" element={<ContentForm />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="logs" element={<LogsList />} />
                  </Route>
                  {/* 404 Route */}
                  <Route path="*" element={<NotFound />} />
                      </Routes>
                      </AccountPanelProvider>
                      </StickyMobileBarProvider>
                    </SearchProvider>
                  </CheckoutProvider>
                  </SiteLogoProvider>
                </ToastProvider>
              </WishlistProvider>
            </CartProvider>
          </AuthProvider>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

export default App;
